export const IRP_SSO_POPUP_MESSAGE_TYPE = 'rapidsos:irp-sso-popup-callback';

const IRP_SSO_POPUP_MARKER_KEY = 'irp-sso-popup-auth';
const IRP_SSO_POPUP_MARKER_VALUE = '1';

export const IRP_SSO_POPUP_WINDOW_NAME = 'rapidsos-irp-sso';
export const IRP_SSO_POPUP_WINDOW_FEATURES =
  'popup=yes,width=520,height=720,resizable=yes,scrollbars=yes';

export type IRPSSOPopupCallbackMessage = {
  type: typeof IRP_SSO_POPUP_MESSAGE_TYPE;
  code: string | null;
  state: string | null;
  error: string | null;
  malformed: boolean;
};

/**
 * Cross-origin parents are intentionally unreadable, but comparing self/top is
 * allowed. Treat an unexpected browser access failure as framed so an
 * interactive IdP is never navigated inside a potentially embedded context.
 */
export const isWindowFramed = (browserWindow: Window = window): boolean => {
  try {
    return browserWindow.self !== browserWindow.top;
  } catch {
    return true;
  }
};

/** Mark the newly opened same-origin window before it leaves for WorkOS. */
export const markIRPSSOPopup = (
  storage: Storage = window.sessionStorage,
): void => {
  storage.setItem(IRP_SSO_POPUP_MARKER_KEY, IRP_SSO_POPUP_MARKER_VALUE);
};

/** Marker reads fail closed when storage is unavailable or sandboxed. */
export const isIRPSSOPopup = (
  storage: Storage = window.sessionStorage,
): boolean => {
  try {
    return (
      storage.getItem(IRP_SSO_POPUP_MARKER_KEY) === IRP_SSO_POPUP_MARKER_VALUE
    );
  } catch {
    return false;
  }
};

export const clearIRPSSOPopupMarker = (
  storage: Storage = window.sessionStorage,
): void => {
  try {
    storage.removeItem(IRP_SSO_POPUP_MARKER_KEY);
  } catch {
    // The popup is closing; cleanup must not obscure the callback relay.
  }
};

const hasCommaSeparatedValue = (
  params: URLSearchParams,
  name: string,
): boolean => params.getAll(name).some(value => value.includes(','));

/**
 * Extract only the WorkOS callback fields that the iframe needs. The launch
 * query, email, and eventual Portal tokens never cross the window boundary.
 */
export const createIRPSSOPopupCallbackMessage = (
  search: string,
): IRPSSOPopupCallbackMessage | null => {
  const params = new URLSearchParams(search);
  const hasCode = params.has('code');
  const hasError = params.has('error');

  if (!hasCode && !hasError) {
    return null;
  }

  const malformed = ['code', 'state', 'error'].some(
    name =>
      params.getAll(name).length > 1 || hasCommaSeparatedValue(params, name),
  );

  const state = params.get('state') || null;
  const code = params.get('code') || null;
  const error = params.get('error') || null;

  return {
    type: IRP_SSO_POPUP_MESSAGE_TYPE,
    code: malformed ? null : code,
    state: malformed ? null : state,
    // Preserve the existing callback rule: an explicit provider error wins
    // over a code, including when its value is empty.
    error: malformed
      ? 'invalid_callback'
      : hasError
        ? error || 'unknown'
        : null,
    malformed,
  };
};

export const isIRPSSOPopupCallbackMessage = (
  value: unknown,
): value is IRPSSOPopupCallbackMessage => {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const candidate = value as Record<string, unknown>;
  const isNullableString = (field: unknown) =>
    field === null || typeof field === 'string';

  return (
    candidate.type === IRP_SSO_POPUP_MESSAGE_TYPE &&
    isNullableString(candidate.code) &&
    isNullableString(candidate.state) &&
    isNullableString(candidate.error) &&
    typeof candidate.malformed === 'boolean'
  );
};
